import React, { useEffect, useState } from 'react'
import { FormProvider, SubmitHandler } from 'react-hook-form'
import useOtpCertForm from 'src/hooks/react-hook-form/useOtpCertForm.hook'
import { FormObjectType, JoinOtpCertFormItemsType } from 'src/types/react-hook-form'
import {
  logoutQueryOption,
  otpCertRequestQueryOption,
  otpCertSubmitQueryOption,
  pushTokenUpdateQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import {
  OtpCertRequestSpace,
  OtpCertSubmitSpace,
  PushTokenUpdateSpace,
  ResponseDataType,
  TelecomType,
} from 'src/types/api'
import { useModal } from 'src/provider/ModalContextProvider'
import {
  ERRCODE_SUCCESS,
  ERRCODES_OTPCERTREQUEST,
  ERRCODES_OTPCERTSUBMIT,
} from 'src/service/constant/errcodes'
import { useToast } from 'src/provider/ToastContextProvider'
import { useCookies } from 'react-cookie'
import { useCountdown } from 'src/hooks/useCountDown.hook'

import { DateUtils } from 'src/utils/date'
import useCustomCookies from 'src/hooks/useCustomCookies.hook'
import { SubmitErrorHandler } from 'react-hook-form/dist/types/form'
import { encryptPhoneNumber } from 'src/utils'
import {
  d30_resignup_dataLayer,
  login_complete_dataLayer,
  otp_request_dataLayer,
} from 'src/utils/gtmDataLayer'
import { Loading } from 'src/components/common/Loading'
import RenderIf from 'src/components/common/RenderIf'
import { useChannel } from 'src/provider/ChannelContextProvider'
import { Bridge } from 'src/utils/bridge'
import useLoginDetector from 'src/hooks/useLoginDetector.hook'
import JoinLinkPageView from 'src/pages/JoinLinkPage/view'
import { useNavigate, useSearchParams } from 'react-router-dom'
import useParamsType from 'src/hooks/useParamsType.hook'
import usePartnerEntry from 'src/hooks/usePartnerEntry.hook'

export interface JoinLinkPageProps {
  registerAttribute: Record<keyof JoinOtpCertFormItemsType, Partial<FormObjectType>>
  onOtpCertRequestSubmit: SubmitHandler<Pick<JoinOtpCertFormItemsType, 'pNumber' | 'agency'>>
  onOtpCertRequestError: SubmitErrorHandler<Pick<JoinOtpCertFormItemsType, 'pNumber' | 'agency'>>
  onOtpCertVerifySubmit: SubmitHandler<JoinOtpCertFormItemsType>
  onOtpCertVerifyError: SubmitErrorHandler<JoinOtpCertFormItemsType>
  count: string
  isOtpStep: boolean
}

const JoinLinkPage = () => {
  const navigate = useNavigate()
  const { openModal } = useModal()
  const { openToast } = useToast()
  const { web } = useChannel()
  const { telecom } = useParamsType()
  const { now, sessionCookieOptions, cookieNoneAppInstallPopupTime } = DateUtils
  const { c_transId, removeAllCookies } = useCustomCookies()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cookies, setCookie, removeCookie] = useCookies([
    'auth-key',
    'u-tel',
    'trans-id',
    'app_install_popup',
  ])
  const { methods, registerAttribute } = useOtpCertForm()
  const { setFocus, resetField, setValue } = methods
  const { isLogin } = useLoginDetector()
  const [isJoinLoading, setIsJoinLoading] = useState(false)
  const [isRetrySubmit, setIsRetrySubmit] = useState(false)
  const [searchParams] = useSearchParams()
  const pNumber = searchParams.get('pNumber') || ''
  const {
    redirectFunction: { handleEnterRedirect, isFreeCouponListLoading },
  } = usePartnerEntry()

  const countExpiredCallbackFn = () => {
    resetField('certNumber')
    openToast({
      contents: '타이머가 만료되었습니다.',
    })
    removeCookie('trans-id', { path: '/' })
  }
  const { countStart, count, countStop } = useCountdown({ countExpiredCallbackFn })

  const { mutateAsync: logoutMutate, isPending: isLogoutPending } =
    useCustomMutation(logoutQueryOption())

  const { mutate: pushTokenUpdateMutate } = useCustomMutation<
    PushTokenUpdateSpace.Request,
    PushTokenUpdateSpace.Response
  >(pushTokenUpdateQueryOption())

  const { mutateAsync: otpRequestMutate, isPending: isOtpRequestPending } = useCustomMutation<
    OtpCertRequestSpace.Request,
    OtpCertRequestSpace.Response
  >(otpCertRequestQueryOption())

  const { mutateAsync: otpSubmit, isPending: isOtpSubmitPending } = useCustomMutation<
    OtpCertSubmitSpace.Request,
    OtpCertSubmitSpace.Response
  >(otpCertSubmitQueryOption())

  const onOtpCertRequestSubmit: SubmitHandler<
    Pick<JoinOtpCertFormItemsType, 'pNumber' | 'agency'>
  > = (data) => {
    const { agency, pNumber } = data
    const digitsPNumber = pNumber.replace(/\D/g, '')
    const submitData = { PNumber: digitsPNumber, Telecom: Number(agency) as TelecomType }

    otpRequestMutate(submitData).then((res: ResponseDataType<OtpCertRequestSpace.Response>) => {
      const { Body, Header } = res
      switch (Header.ErrCode) {
        case ERRCODE_SUCCESS:
          openToast({ contents: '인증번호를 전송하였습니다.' })
          otp_request_dataLayer()
          countStart(Body.TimeOut)
          setCookie('trans-id', Body.TransID, {
            path: '/',
            expires: now().add(Body.TimeOut, 'seconds').toDate(),
          })
          setTimeout(() => {
            setFocus('certNumber')
          }, 100)
          break
        case ERRCODES_OTPCERTREQUEST.ERROR_30DAYS_LIMIT:
          openModal({
            contents: { d: Header.ErrMsg },
            button: { submit: { t: '확인' } },
          })
          d30_resignup_dataLayer()
          break
        case ERRCODES_OTPCERTREQUEST.ERROR_YEAR_RE_ENTRY_LIMIT:
        case ERRCODES_OTPCERTREQUEST.ERROR_AUTH_MSG_TIME:
          openModal({
            contents: { d: Header.ErrMsg },
            button: { submit: { t: '확인' } },
          })
          break
        case ERRCODES_OTPCERTREQUEST.BLOCKED_USERS:
          openModal({
            contents: { d: Header.ErrMsg },
            button: { submit: { t: '확인', f: () => resetField('pNumber') } },
          })
          break
        case ERRCODES_OTPCERTREQUEST.ERROR_TELECOM:
        case ERRCODES_OTPCERTREQUEST.ERROR_PHONENUMBER:
        case ERRCODES_OTPCERTREQUEST.ERROR_IS_NOT_OPEN_TELECOM:
        default:
          openToast({
            contents: Header.ErrMsg,
          })
          break
      }
    })
  }

  const onOtpCertRequestError: SubmitErrorHandler<
    Pick<JoinOtpCertFormItemsType, 'pNumber' | 'agency'>
  > = (errors, event) => {
    if (!pNumber) {
      openToast({ contents: '휴대폰 번호를 입력해주세요.' })
      return
    } else if (errors.pNumber !== undefined) {
      openToast({ contents: errors.pNumber.message as string })
    }
  }

  const onOtpCertVerifySubmit: SubmitHandler<JoinOtpCertFormItemsType> = (data) => {
    const { certNumber } = data

    if (!c_transId && !isRetrySubmit) {
      openToast({ contents: '인증 완료 후 이용해주세요.' })
      return
    }

    if (!c_transId && isRetrySubmit) {
      openToast({ contents: '인증번호를 재전송해주세요.' })
      return
    }

    if (certNumber.length === 0) {
      openToast({ contents: '인증번호를 입력해주세요.' })
      return
    }

    const submitData = {
      TransID: c_transId,
      ApprovalNumber: certNumber,
    }

    otpSubmit(submitData).then(async (res: ResponseDataType<OtpCertSubmitSpace.Response>) => {
      const { Body, Header } = res
      const { AuthKey, IsJoin, PNumber } = Body

      switch (Header.ErrCode) {
        case ERRCODE_SUCCESS:
          const encryptPNumber = `${encryptPhoneNumber(PNumber)}`
          removeCookie('trans-id', { path: '/' })

          if (IsJoin) {
            setIsJoinLoading(true)

            setCookie('u-tel', encryptPNumber, sessionCookieOptions())
            setCookie('auth-key', AuthKey, sessionCookieOptions())
            Bridge.handleReqSaveAuthKey(AuthKey)
            Bridge.getPushToken.req()

            /** 리다이렉트 제어 */
            setCookie('app_install_popup', true, {
              path: '/',
              expires: cookieNoneAppInstallPopupTime(),
            })
            handleEnterRedirect('free_coupon')

            openToast({ contents: '로그인되었습니다.' })
            login_complete_dataLayer()
          } else {
            openModal({
              contents: { d: '비가입자입니다.' },
              button: { submit: { t: '확인', f: () => navigate(`/web/splash`) } },
            })
          }
          break
        case ERRCODES_OTPCERTSUBMIT.ERROR_WRONG_APPROVAL_NUMBER:
          openToast({ contents: Header.ErrMsg })
          resetField('certNumber')
          !web && setFocus('certNumber')
          break
        case ERRCODES_OTPCERTSUBMIT.ERROR_EXPIRED_TIME:
        default:
          openToast({ contents: Header.ErrMsg })
          resetField('certNumber')
          break
        case ERRCODES_OTPCERTSUBMIT.ERROR_EXPIRATION_APPROVAL_NUMBER:
          countStop()
          setIsRetrySubmit(true)
          resetField('certNumber')
          openToast({ contents: Header.ErrMsg })
          removeCookie('trans-id', { path: '/' })
          break
      }
    })
  }

  const onOtpCertVerifyError: SubmitErrorHandler<JoinOtpCertFormItemsType> = (errors, event) => {
    if (!c_transId && errors.pNumber !== undefined) {
      openToast({ contents: errors.pNumber.message as string })
    }
  }

  useEffect(() => {
    //초기화
    removeCookie('trans-id', { path: '/' })
    localStorage.clear()
    if (isLogin) {
      logoutMutate({}).finally(() => {
        removeAllCookies()
      })
    }

    Bridge.getPushToken.res((pushToken: string) => {
      pushTokenUpdateMutate({ PushToken: pushToken })
    })
    setValue('agency', Number(telecom) as TelecomType)
    setValue('pNumber', pNumber.replace(/\D/g, '').slice(0, 11))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const props: JoinLinkPageProps = {
    registerAttribute,
    onOtpCertRequestError,
    onOtpCertRequestSubmit,
    onOtpCertVerifyError,
    onOtpCertVerifySubmit,
    count,
    isOtpStep: !!cookies['trans-id'],
  }

  return (
    <FormProvider {...methods}>
      <RenderIf
        when={
          isOtpRequestPending ||
          isOtpSubmitPending ||
          isJoinLoading ||
          isFreeCouponListLoading ||
          isLogoutPending
        }
      >
        <Loading.Default />
      </RenderIf>
      <JoinLinkPageView {...props} />
    </FormProvider>
  )
}

export default JoinLinkPage
